import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { Target, Plus, Trash2, Wand2, Check, Square } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import {
  useSetDetail,
  useToggleSetItemOwned,
  useAddSetItem,
  useRemoveSetItem,
  useAutoMatchSetItems,
  useDeleteSet,
} from '../hooks/useSets';
import { useTranslation } from '../i18n';
import { useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import styles from './sets.$setId.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sets/$setId',
  component: SetDetailPage,
});

function SetDetailPage() {
  const { setId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useSetDetail(setId);
  const toggleOwned = useToggleSetItemOwned();
  const addItem = useAddSetItem();
  const removeItem = useRemoveSetItem();
  const autoMatch = useAutoMatchSetItems();
  const deleteSet = useDeleteSet();
  const [newItemName, setNewItemName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <Shell>
        <div className={styles.loading}>{t('common.loading')}</div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <div className={styles.loading}>{t('common.notFound')}</div>
      </Shell>
    );
  }

  function handleAddItem() {
    if (!newItemName.trim()) return;
    addItem.mutate({ setId, name: newItemName.trim() });
    setNewItemName('');
  }

  function handleDelete() {
    if (confirmDelete) {
      deleteSet.mutate(setId, {
        onSuccess: () => navigate({ to: '/sets' }),
      });
    } else {
      setConfirmDelete(true);
    }
  }

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Target size={20} strokeWidth={1.75} />
              {data.name}
            </h1>
            {data.platform && <span className={styles.platformBadge}>{data.platform}</span>}
            {data.description && <p className={styles.desc}>{data.description}</p>}
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.autoMatchBtn}
              onClick={() => autoMatch.mutate(setId)}
              disabled={autoMatch.isPending}
            >
              <Wand2 size={14} strokeWidth={2} />
              {autoMatch.isPending ? t('sets.matching') : t('sets.autoMatch')}
            </button>
            <button
              className={`${styles.deleteBtn} ${confirmDelete ? styles.deleteBtnConfirm : ''}`}
              onClick={handleDelete}
              disabled={deleteSet.isPending}
            >
              <Trash2 size={14} strokeWidth={2} />
              {confirmDelete ? t('sets.confirmDelete') : t('sets.delete')}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className={styles.progressSection}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${data.completionPercent}%` }}
            />
          </div>
          <div className={styles.progressLabel}>
            {data.ownedItems} / {data.totalItems} ({data.completionPercent}%)
          </div>
        </div>

        {/* Auto-match result */}
        {autoMatch.data && (
          <div className={styles.matchResult}>
            {autoMatch.data.matchCount > 0
              ? `${autoMatch.data.matchCount} ${t('sets.itemsMatched')}`
              : t('sets.noMatchesFound')}
          </div>
        )}

        {/* Add item */}
        <div className={styles.addItemRow}>
          <input
            className={styles.addItemInput}
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder={t('sets.addItemPlaceholder')}
            onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          />
          <button
            className={styles.addItemBtn}
            onClick={handleAddItem}
            disabled={!newItemName.trim() || addItem.isPending}
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        </div>

        {/* Checklist */}
        <div className={styles.checklist}>
          {data.items.map((item) => (
            <div key={item.id} className={`${styles.checkItem} ${item.isOwned ? styles.checkItemOwned : ''}`}>
              <button
                className={styles.checkbox}
                onClick={() =>
                  toggleOwned.mutate({
                    setId,
                    itemId: item.id,
                    isOwned: !item.isOwned,
                  })
                }
              >
                {item.isOwned ? (
                  <Check size={14} strokeWidth={2.5} />
                ) : (
                  <Square size={14} strokeWidth={1.75} />
                )}
              </button>
              {item.inventoryItemImageUrl && (
                <img
                  src={item.inventoryItemImageUrl}
                  alt=""
                  className={styles.checkThumb}
                />
              )}
              <span className={`${styles.checkName} ${item.isOwned ? styles.checkNameOwned : ''}`}>
                {item.name}
              </span>
              <button
                className={styles.removeBtn}
                onClick={() => removeItem.mutate({ setId, itemId: item.id })}
              >
                <Trash2 size={12} strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
